import { useMutation, useQueryClient } from '@tanstack/react-query';
import { utils, writeFileXLSX } from 'xlsx';

import postImportSchedulePayload from './model/request/postImportSchedulePayload';
import postImportSchedule from 'Api/schedule/postImportSchedule';
import { useScheduleState } from 'View/schedule/state';
import { useModal } from 'Store/modal';
import { importScheduleText } from 'Const/schedule';
import { useToast } from 'Store/toast';
import DialogErrorUpload from 'View/schedule/component/dialog-error-upload';

const usePostImportSchedule = ({ setFile, filters }) => {
  const queryClient = useQueryClient();
  const { updateState, resetAlertProps } = useScheduleState();
  const { onOpenModal, onResetModal } = useModal();
  const { onOpenToast } = useToast();

  return useMutation({
    mutationFn: async ({ file }) => {
      const payload = postImportSchedulePayload(file);

      setFile(null);

      updateState({ isUploadLoad: true });

      const response = await postImportSchedule({ payload });

      return response;
    },

    onSuccess: (response) => {
      const { data, status } = response;
      updateState({ isUploadLoad: false, uploadLoadValue: 0, isOpenFormUpload: false, alertProps: {} });
      queryClient.invalidateQueries({ queryKey: ['schedules', filters] });

      switch (status) {
        case 200:
          onOpenToast({
            status: 'success',
            title: 'Success',
            position: 'top',
            description: (
              <>
                <span style={{ color: '#33995E' }}>
                  {data?.data?.successCount} dari {data?.data?.successCount}
                </span>{' '}
                schedule berhasil diunggah.{' '}
              </>
            ),
            duration: 5,
            alwaysShow: false,
          });

          break;

        case 202:
          const exportJson = data.errors?.map((error) => {
            return {
              Status: error.status,
              ID: error.id,
              'Nama Marketplace': error.namaMarketplace,
              Tanggal: error.tanggal,
              Time: error.time,
              'Nama Host': error.namaHost,
              'Nama Brand': error.namaBrand,
              'Nama Studio': error.namaStudio,
              'Nama Operator': error.namaOperator,
              'Nama Account': error.namaAccount,
              'Error Message': error.errorMessage,
            };
          });

          const ws = utils.json_to_sheet(exportJson);
          const wb = utils.book_new();
          utils.book_append_sheet(wb, ws, 'Data');

          onOpenModal({
            isOpen: true,
            type: !data.successCount ? 'error' : 'warning',
            title: importScheduleText.failedTitle,
            contentTitle: !data.successCount ? importScheduleText.failedContentTitle : importScheduleText.warningContentTitle,
            contentDescription: !data.successCount ? importScheduleText.failedContentDesc : importScheduleText.warningContentDesc,
            showPrimaryButton: true,
            primaryButtonText: importScheduleText.failedPrimaryButtonText,
            showSecondaryButton: true,
            secondaryButtonText: importScheduleText.failedSecondaryButtonText,
            otherContent: <DialogErrorUpload data={data} />,
            onClickSecondaryButton: () => onResetModal(),
            onClickPrimaryButton: () => {
              writeFileXLSX(wb, 'Schedule Upload Error.xlsx');
              onOpenToast({
                status: 'success',
                title: 'Success',
                position: 'top',
                description: importScheduleText.successDownloadData,
                duration: 5,
                alwaysShow: false,
              });
              onResetModal();
            },
          });

          break;

        default:
          break;
      }
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.errors?.length > 0 ? error?.response?.data?.errors[0]?.message : importScheduleText.errorUploadScheduleDescServer;

      onResetModal();

      updateState({
        isUploadLoad: false,
        uploadLoadValue: 0,
        isOpenFormUpload: false,
        alertProps: {
          isOpen: true,
          title: importScheduleText.errorUploadScheduleText,
          description: errorMessage,
          type: 'error',
          onClose: () => resetAlertProps(),
        },
      });
    },
  });
};

export default usePostImportSchedule;
